/**
 * The company's base currency — the label every bare number on the wire gets.
 *
 * Money arrives from Frappe as a plain number with no currency of its own (CLAUDE.md's API
 * conventions), so every mapper has to attach one. This module is where that one value lives.
 *
 * ---- It is the server's value now, not ours ----
 *
 * This used to export `DEFAULT_BASE_CURRENCY = 'AED'`, a constant six modules imported, because
 * `settings.get_pricing_rules.base_currency` read `null` on the live bench — defect `D5`. It
 * returns `"AED"` as of 2026-08-06, so the hardcode is gone: `useSettings.ts`'s `usePricingRules`
 * calls `setBaseCurrency` the moment it resolves, and everything else reads `getBaseCurrency()`.
 *
 * ---- ⚠️ KNOWN INCORRECT as of 2026-08-07: Bosun is multi-company ----
 *
 * This module assumed **one company, chosen in settings, applied app-wide** — what the business
 * told us on 2026-08-05. **The Frappe team has since corrected that: there will be multiple
 * companies.** Currency follows company, so a single module-level base currency is now the wrong
 * shape, not merely a simplification: `data_ops.get_company_default_currency` returns seven
 * companies across FOUR currencies (`AED`, `SAR`, `INR`…), and we label every amount with one.
 *
 * A mislabelled amount is worse than a missing one — `SAR 1,000` shown as `AED 1,000` reads as a
 * correct figure. It is left in place only because `Enquiry` has no `company` field to pick
 * between them, so there is nothing better to read. See `company.ts` for the same defect and
 * ask `E11` (`company` on `Enquiry`) for what retires both.
 *
 * ---- Why a module-level value and not a hook ----
 *
 * The consumers are pure mappers (`toEnquirySummary`, `toOffer`, `toQuotation`…) running inside
 * `queryFn`s, which cannot call hooks. This is the same idiom `client.ts`'s
 * `setActingRole`/`getActingRole` already uses for the acting role, for the same reason.
 *
 * ---- The fallback is a last resort, not a default ----
 *
 * A mapper can run before the settings query resolves, so there is always a window with no value.
 * `FALLBACK_BASE_CURRENCY` fills it. It is deliberately NOT used when the server answers with
 * something unusable — `setBaseCurrency` refuses `null`/empty and warns, because silently
 * installing an empty currency would render every amount in the app unlabelled.
 */

/**
 * Used only until `usePricingRules` resolves, and if it never does.
 *
 * `AED` because that is what the live bench's `base_currency` returns and what its enquiries use.
 * This is a stand-in for a value we now genuinely read — not a business rule, and not a claim
 * about which company an enquiry belongs to.
 */
export const FALLBACK_BASE_CURRENCY = 'AED'

let current: string | null = null
let warned = false

/** Read by the mappers. Never empty. */
export function getBaseCurrency(): string {
  return current ?? FALLBACK_BASE_CURRENCY
}

/**
 * Install the server's value. Called by `usePricingRules` (`useSettings.ts`).
 *
 * Ignores anything unusable rather than overwriting a working value with it — `null` is precisely
 * what this endpoint sent for months, and accepting it would be worse than never having asked.
 */
export function setBaseCurrency(value: string | null | undefined): void {
  const next = typeof value === 'string' ? value.trim() : ''
  if (next === '') {
    if (!warned) {
      warned = true
      console.warn(
        '[settings.get_pricing_rules] base_currency came back empty — keeping ' +
          `${getBaseCurrency()}. Money labels are a fallback, not the server's answer.`,
      )
    }
    return
  }
  current = next
}

/* ============================================================================================
 * Per-company base currency
 * ============================================================================================ */

/**
 * Every company's own base currency, by company name.
 *
 * Bosun is multi-company and currency follows company — measured 2026-08-08, SEVEN companies
 * across FOUR currencies:
 *
 *   AQUA MARIS SHIPS SUPPLY … AED    BEUREX TRADING COMPANY W.L.L      BHD
 *   BRAVIO GROUP COMPANY      AED    GREENSPIRE Maritime               SAR
 *   GREENSPIRE … SERVICES LLC AED    GREENSPIRE … PRIVATE LIMITED      INR
 *                                    GREENSPIRE … TRADING LLC BRANCH   SAR
 *
 * The single app-wide value above labels an INR company's money as AED, and `SAR 1,000` shown as
 * `AED 1,000` reads as a correct figure — a mislabelled amount is worse than a missing one.
 *
 * This module's own header said that was unavoidable "because `Enquiry` has no `company` field to
 * pick between them". That is no longer true: `Enquiry.company` exists, is populated, and
 * `get_enquiry` returns it. So a caller holding an enquiry can now resolve the right one.
 */
const byCompany = new Map<string, string>()

/** Install the company → currency map. Called once by `useCompanyCurrencies` (`useMasters.ts`). */
export function setCompanyCurrencies(entries: Iterable<readonly [string, string]>): void {
  for (const [company, currency] of entries) {
    const name = company.trim()
    const value = currency.trim()
    // Same rule as `setBaseCurrency`: an unusable value never overwrites a working one.
    if (name !== '' && value !== '') byCompany.set(name, value)
  }
}

/**
 * The base currency for one company, falling back to the app-wide value.
 *
 * The fallback is reached for an enquiry with no company (every enquiry Bosun creates from a mail,
 * until `create_enquiry` accepts the field — ask `E11`) and before the map has loaded. Both are
 * real states, and both are why this returns a currency rather than `null`: money has to be
 * labelled with something, and the app-wide value is the closest honest guess.
 */
export function baseCurrencyFor(company: string | null | undefined): string {
  const name = typeof company === 'string' ? company.trim() : ''
  if (name === '') return getBaseCurrency()
  return byCompany.get(name) ?? getBaseCurrency()
}

/** Whether a company's own currency is actually known — for a caller that wants to say so. */
export function hasCompanyCurrency(company: string | null | undefined): boolean {
  const name = typeof company === 'string' ? company.trim() : ''
  return name !== '' && byCompany.has(name)
}

/** Tests only — module state persists across cases otherwise. */
export function __resetBaseCurrencyForTests(): void {
  current = null
  warned = false
  byCompany.clear()
}
